import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { DatabaseIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import AnalyticsDatePicker from 'dashboard/components/AnalyticsDatePicker';
import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ReactSelect from 'dashboard/components/ReactSelect';
import Tooltip from 'dashboard/components/Tooltip';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import type { InquiryTemplate } from 'dashboard/models/inquiry-template';
import {
  MetricsTemplateFunnel,
  MetricsTemplateFunnelFlow,
  MetricsVolumeSummary,
} from 'dashboard/models/metrics';
import { Template } from 'dashboard/models/template';
import { MetricsState } from 'dashboard/redux/reducers/metrics-inquiry';
import InquiryFunnelAnalyticsFlowVisualizer from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelAnalyticsFlowVisualizer';
import { metricsCard } from 'dashboard/views/DashboardInquiryAnalyticsFunnelView/TemplateMetrics';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { Heading, Label } from 'design-system/components/Text';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import { ApiModel } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

const SearchAndFilterCardBody = styled(Card.Body)`
  grid-gap: 0;
  grid-template-columns: 1fr auto;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const TooltipContainer = styled('div')`
  max-width: 500px;
`;

const SearchAndFilterContainer = styled.div`
  flex: auto;
  display: grid;
  align-items: end;
  grid-template-columns:
    minmax(215px, 1.5fr) minmax(200px, 2fr) minmax(130px, 1fr) minmax(180px, 1.5fr)
    auto;
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const RunContainer = styled.div`
  margin-left: ${Spacing[4]};
  // Negative top margin to align it all
  margin-top: 6px;
  min-width: 225px;
  display: grid;
  // 16px label + 8px margin bottom
  grid-template-rows: 24px 1fr;
  justify-items: right;
`;

const SelectContainer = styled.div``;

const AbsoluteSelectContainer = styled.div``;

const InquiryTemplateVersionContainer = styled.div``;

const StyledSelect = styled(ReactSelect<string>)`
  padding: 0px ${Spacing[2]};
`;

const RunButton = styled(Button)`
  height: ${Spacing[6]};
  float: right;
`;

interface DashboardInquiryAnalyticsFunnelV2ViewProps
  extends Pick<
    MetricsState,
    | 'filters'
    | 'templateFunnelFlows'
    | 'loadedTemplateOrInquiryTemplateId'
    | 'loadedInquiryTemplateVersionId'
  > {
  // This is the template that is selected in the drop down
  selectedTemplate?: Template | InquiryTemplate;
  // This is the template that was/will be loaded for display
  templateToShow: Template | InquiryTemplate;
  templates: (Template | InquiryTemplate)[];
  templateFunnel?: MetricsTemplateFunnel;
  templateMetricsVolumeSummary?: MetricsVolumeSummary;
  queriedAt: Iso8601DateString;
  updatedAt: Iso8601DateString;
  isUnpublishedTemplate: boolean;
  loading: boolean;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
  handleInquiryTemplateVersionIdUpdate: (inquiryTemplateVersionId: string) => void;
  handleTemplateFilterUpdate: (templateIds: string) => void;
  loadMetrics: () => Promise<void>;
}

const CardBody = styled(Card.Body)`
  position: relative;
`;

const StyledInfoIcon = styled(DatabaseIcon)`
  margin-bottom: -${Spacing[1]};
`;

const StyledLabel = styled(FormControlLabel)`
  margin-left: ${Spacing[2]};
`;

// unfortunately we need to support both dynamic flow and legacy templates.
// If it is dynamic flow, we use our very nice new flow visualizer.
// If it is legacy, we render the old view.
const renderTemplateOrInquiryTemplateFunnel = (
  templateFunnelFlow: MetricsTemplateFunnelFlow,
  loading: boolean,
  isAbsolute: boolean,
  templateToShow: Template | InquiryTemplate,
  isUnpublishedTemplate: boolean,
  templateFunnel?: MetricsTemplateFunnel,
  templateMetricsVolumeSummary?: MetricsVolumeSummary,
) => {
  if (loading || templateToShow == null) {
    return null;
  }
  if (templateToShow.type === ApiModel.Template) {
    return metricsCard(
      0,
      loading,
      templateToShow.id,
      templateToShow.attributes.name,
      templateToShow.attributes['steps-config'].steps || [],
      () => null,
      templateFunnel,
      templateMetricsVolumeSummary,
    );
  }
  if (isUnpublishedTemplate) {
    return (
      <Card>
        <CardBody>
          <TableNullState dataTestBlock='template-funnel-chart'>
            <Heading size='sm' data-test='template-funnel-chart__nonlinear'>
              The funnel for this template cannot be displayed because no published template was
              found.
            </Heading>
          </TableNullState>
        </CardBody>
      </Card>
    );
  }
  if (
    templateFunnelFlow == null ||
    templateFunnelFlow.steps == null ||
    templateFunnelFlow.steps.length === 0
  ) {
    return null;
  }
  return (
    <Card>
      <CardBody>
        <Card.Title data-test='inquiry-funnel-funnel-flow'>Funnel flow</Card.Title>
        <InquiryFunnelAnalyticsFlowVisualizer
          templateFunnelFlow={templateFunnelFlow}
          template={templateToShow}
          isAbsolute={isAbsolute}
        />
      </CardBody>
    </Card>
  );
};

export const ABSOLUTE_RELATIVE_OPTIONS = [
  {
    label: 'Overall',
    value: 'absolute',
  },
  {
    label: 'Per Step',
    value: 'relative',
  },
];

const DashboardInquiryAnalyticsFunnelV2View = ({
  selectedTemplate,
  templateToShow,
  filters,
  templates,
  templateFunnelFlows,
  templateFunnel,
  templateMetricsVolumeSummary,
  loadedTemplateOrInquiryTemplateId,
  loadedInquiryTemplateVersionId,
  queriedAt,
  updatedAt,
  isUnpublishedTemplate,
  loading,
  handleDateUpdateV2,
  handleInquiryTemplateVersionIdUpdate,
  handleTemplateFilterUpdate,
  loadMetrics,
}: DashboardInquiryAnalyticsFunnelV2ViewProps) => {
  const { createdAtStart, createdAtEnd } = filters;

  const dateRange = useMemo<AnalyticsDateRange>(() => {
    return {
      startDate: createdAtStart ? DateTime.fromISO(createdAtStart, { setZone: true }) : undefined,
      endDate: createdAtEnd ? DateTime.fromISO(createdAtEnd, { setZone: true }) : undefined,
    };
  }, [createdAtStart, createdAtEnd]);

  // Cast as a date
  const [isAbsolute, setIsAbsolute] = useState<boolean>(true);
  const templateFunnelFlow =
    loadedInquiryTemplateVersionId == null
      ? templateFunnelFlows[0]
      : (templateFunnelFlows.find((flow) => flow.token === loadedInquiryTemplateVersionId) ??
        templateFunnelFlows[0]);
  return (
    <>
      <Card>
        <SearchAndFilterCardBody>
          <SearchAndFilterContainer>
            <AnalyticsDatePicker
              dataTest='metrics-template-view__date-range'
              handleDatesChangeV2={handleDateUpdateV2}
              dateRange={dateRange}
              disabled={loading}
            />
            {selectedTemplate != null && templates.length > 1 && (
              <SelectContainer>
                <StyledLabel>Template</StyledLabel>
                <StyledSelect
                  size='sm'
                  id='checklist-item-input__select-templates'
                  name='checklistItemRequiredForResolutions'
                  value={selectedTemplate.id}
                  options={templates.map((template) => {
                    return {
                      label: template.attributes.name,
                      value: template.id,
                    };
                  })}
                  disabled={loading}
                  onChange={handleTemplateFilterUpdate}
                  portal
                />
              </SelectContainer>
            )}
            {selectedTemplate != null && selectedTemplate.type === ApiModel.InquiryTemplate && (
              <AbsoluteSelectContainer>
                <Tooltip
                  trigger='hover'
                  tooltip={
                    <TooltipContainer>
                      Select Overall to get the % of started inquiries that made it to each step.
                      Select Per Step to get the % of inquiries that made it from the previous step.
                    </TooltipContainer>
                  }
                  hideArrow={false}
                >
                  <StyledLabel>Funnel metric</StyledLabel>
                </Tooltip>
                <StyledSelect
                  id='checklist-item-input__select-conversion-rate'
                  name='checklistItemRequiredForResolutions'
                  onChange={(val: string) => {
                    setIsAbsolute(val === 'absolute');
                  }}
                  value={
                    isAbsolute
                      ? ABSOLUTE_RELATIVE_OPTIONS[0].value
                      : ABSOLUTE_RELATIVE_OPTIONS[1].value
                  }
                  options={ABSOLUTE_RELATIVE_OPTIONS}
                  portal={true}
                  isMulti={false}
                  isSearchable={false}
                  isClearable={false}
                  disabled={loading}
                  size='sm'
                />
              </AbsoluteSelectContainer>
            )}
            {templateFunnelFlows.length > 0 &&
              selectedTemplate != null &&
              selectedTemplate.type === ApiModel.InquiryTemplate &&
              selectedTemplate.id === loadedTemplateOrInquiryTemplateId && (
                <InquiryTemplateVersionContainer>
                  <Tooltip
                    trigger='hover'
                    tooltip={
                      <TooltipContainer>
                        Any changes to an inquiry template generates a new template version. Select
                        a specific template version to view relevant Funnel metrics.
                      </TooltipContainer>
                    }
                    hideArrow={false}
                  >
                    <StyledLabel>Template Version</StyledLabel>
                  </Tooltip>
                  <StyledSelect
                    id='checklist-item-input__select-template-funnel-flows'
                    name='checklistItemRequiredForResolutions'
                    onChange={(val: string) => {
                      handleInquiryTemplateVersionIdUpdate(val);
                    }}
                    value={templateFunnelFlow.token}
                    options={templateFunnelFlows.map((templateFunnel) => {
                      return {
                        value: templateFunnel.token,
                        label: templateFunnel.updated_at
                          ? DateTime.fromISO(templateFunnel.updated_at).toFormat(
                              DATE_AND_TIME_FORMAT,
                            )
                          : 'Unknown',
                      };
                    })}
                    portal={true}
                    isMulti={false}
                    isSearchable={false}
                    isClearable={false}
                    disabled={loading}
                    size='sm'
                  />
                </InquiryTemplateVersionContainer>
              )}
            <div>
              <RunContainer>
                {queriedAt != null && (
                  <Label size='md' color='tertiary' style={{ float: 'right' }} weight='bold'>
                    Last ran {DateTime.fromISO(queriedAt).toRelative()}
                    {updatedAt != null && (
                      <HelpIcon icon={<StyledInfoIcon />}>
                        Data is up to date as of {DateTime.fromISO(updatedAt).toRelative()}
                      </HelpIcon>
                    )}
                  </Label>
                )}
                <RunButton
                  id='metrics-template-view__run'
                  data-test='metrics-template-view__run'
                  onClick={loadMetrics}
                  isPending={loading}
                  disabled={loading}
                  width='auto'
                >
                  Run
                </RunButton>
              </RunContainer>
            </div>
          </SearchAndFilterContainer>
        </SearchAndFilterCardBody>
      </Card>
      {renderTemplateOrInquiryTemplateFunnel(
        templateFunnelFlow,
        loading,
        isAbsolute,
        templateToShow,
        isUnpublishedTemplate,
        templateFunnel,
        templateMetricsVolumeSummary,
      )}
    </>
  );
};

export default DashboardInquiryAnalyticsFunnelV2View;
